// Webex Connect SPA API client — browser-direct against connectapi/v1.
// Auth: short-lived `accesstoken` UUID header grabbed from the Connect SPA
// (DevTools → any connectapi request → Request Headers → accesstoken).
// CORS: Connect returns `access-control-allow-origin: *` and permits the
// `accesstoken` header, so direct-from-browser works from any origin.
//
// All calls are serialised with a small gap (`X-Rate-Limit-Remaining` is
// returned on each response — ~1 req/300ms is comfortably within the budget
// observed in the Connect SPA's own traffic).

const RATE_LIMIT_GAP_MS = 300;

let lastCallAt = 0;
async function pace() {
  const since = Date.now() - lastCallAt;
  if (since < RATE_LIMIT_GAP_MS) {
    await new Promise((r) => setTimeout(r, RATE_LIMIT_GAP_MS - since));
  }
  lastCallAt = Date.now();
}

function tick() {
  return Date.now();
}

async function callJson(baseUrl, path, { method = 'GET', token, body, query } = {}) {
  await pace();
  const url = new URL(path, baseUrl);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  url.searchParams.set('tick', String(tick()));
  const res = await fetch(url.toString(), {
    method,
    headers: {
      accept: 'application/json, text/plain, */*',
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      accesstoken: token,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(
      `${method} ${path} → ${res.status}: ${text.slice(0, 200)}`,
    );
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`${method} ${path} → non-JSON response: ${text.slice(0, 200)}`);
  }
}

// ---------- Custom Nodes (Integrations) ----------

export async function listIntegrations(baseUrl, token) {
  const data = await callJson(baseUrl, '/connectapi/v1/nodeMgmt/integrations', {
    token,
  });
  return data.integrations || [];
}

export async function listMethodsForIntegration(baseUrl, token, assetId) {
  const data = await callJson(
    baseUrl,
    `/connectapi/v1/nodeMgmt/nodemethod/${assetId}`,
    { token },
  );
  return data.nodemethods || [];
}

export async function getMethodDetail(baseUrl, token, assetId, methodId) {
  const data = await callJson(
    baseUrl,
    `/connectapi/v1/nodeMgmt/nodemethod/${assetId}/${methodId}`,
    { token },
  );
  return data.nodemethod;
}

// ---------- Services + Flows ----------

export async function listServices(baseUrl, token, { pageSize = 100 } = {}) {
  const data = await callJson(baseUrl, '/connectapi/v1/service/search', {
    method: 'POST',
    token,
    body: {
      pageno: 1,
      pagesize: pageSize,
      searchtext: '',
      servicestatus: '',
      sortby: '',
      healthstatus: '',
    },
  });
  return data.servicedetails || [];
}

export async function getServiceDetail(baseUrl, token, serviceId) {
  const data = await callJson(baseUrl, `/connectapi/v1/service/${serviceId}`, {
    token,
  });
  return data.servicedetails;
}

export async function listFlowsForService(baseUrl, token, serviceId) {
  const data = await callJson(
    baseUrl,
    `/connectapi/v1/service/${serviceId}/flow`,
    { token },
  );
  return data.flows || [];
}

// Flow export returns a hex-encoded opaque binary blob — same artifact Connect's
// own Export button produces. Not JSON. We keep it as hex in the bundle; the
// Transfer path hex-decodes to bytes before POSTing to the target's /flow route.
//
// Transient "Failed to fetch" errors have been observed mid-batch (network
// blip, source-session hiccup, or occasional response-stream drop on larger
// flows). Retry up to twice with a short backoff before giving up.
export async function exportFlow(
  baseUrl,
  token,
  { serviceId, flowId, paletteName, paletteType },
) {
  const url = new URL(
    `/connectapi/v1/service/${serviceId}/flow/export`,
    baseUrl,
  );
  url.searchParams.set('paletteName', paletteName);
  url.searchParams.set('flowId', String(flowId));
  url.searchParams.set('paletteType', paletteType);
  url.searchParams.set('accessToken', token);

  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    await pace();
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, 500 * attempt));
    }
    try {
      const res = await fetch(url.toString(), {
        headers: {
          accept: 'application/json, text/plain, */*',
          accesstoken: token,
        },
      });
      const text = await res.text();
      if (!res.ok) {
        throw new Error(
          `flow export ${serviceId}/${flowId} → ${res.status}: ${text.slice(0, 200)}`,
        );
      }
      return text;
    } catch (e) {
      lastErr = e;
      // Only retry on transient network/stream failures. A 4xx/5xx with a
      // response body is a server-side rejection — fail fast.
      if (!/Failed to fetch|NetworkError|network/.test(String(e.message))) {
        throw e;
      }
    }
  }
  throw lastErr;
}

// ---------- Import (Transfer target side) ----------
//
// Create endpoints decoded from a round-trip HAR captured against
// presidiogold.us.webexconnect.io.

// POST /nodeMgmt/customnode is multipart form-data with a single `customnode`
// field carrying stringified JSON. Returns `{ nodeId }` (the new asset_id).
export async function createCustomNode(
  baseUrl,
  token,
  { name, description = '', categoryId = '1166', customNodeType = 'rest' } = {},
) {
  await pace();
  const url = new URL('/connectapi/v1/nodeMgmt/customnode', baseUrl);
  url.searchParams.set('tick', String(tick()));
  const fd = new FormData();
  fd.append(
    'customnode',
    JSON.stringify({
      name,
      description,
      customNodeType,
      categoryId,
      categoryName: '',
      integrationtype: 0,
      copyIntegrationId: 0,
    }),
  );
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { accesstoken: token },
    body: fd,
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(
      `createCustomNode ${name} → ${res.status}: ${text.slice(0, 200)}`,
    );
  }
  const parsed = JSON.parse(text);
  if (parsed.code !== '0') {
    throw new Error(
      `createCustomNode ${name} rejected: ${parsed.description || text.slice(0, 200)}`,
    );
  }
  return parsed.nodeId;
}

// POST /nodeMgmt/nodemethod is plain JSON. Source `extraparams` is a
// stringified JSON blob — target wants the parsed object. Source `method_type`
// is an integer code (1=get, 2=post, 3=put, …); target wants the HTTP verb
// string, which lives in `extraparams.method`.
export async function createNodeMethod(
  baseUrl,
  token,
  { targetAssetId, sourceMethodDetail },
) {
  const extraRaw = sourceMethodDetail.extraparams;
  const extra =
    typeof extraRaw === 'string' ? JSON.parse(extraRaw) : { ...extraRaw };
  if (extra && typeof extra === 'object') extra.reference_id = '';

  const verb =
    (extra && extra.method) || String(sourceMethodDetail.method_type || 'post');

  const body = {
    method_name: sourceMethodDetail.method_name,
    asset_id: String(targetAssetId),
    method_url: sourceMethodDetail.method_url || (extra && extra.url) || '',
    method_type: verb.toLowerCase(),
    helptext: sourceMethodDetail.helptext || '',
    extraparams: extra,
  };

  const data = await callJson(baseUrl, '/connectapi/v1/nodeMgmt/nodemethod', {
    method: 'POST',
    token,
    body,
  });
  if (data.code !== '0') {
    throw new Error(
      `createNodeMethod ${sourceMethodDetail.method_name} rejected: ${data.description || 'unknown'}`,
    );
  }
  return data.reference_id;
}

// POST /service with plain JSON { name, status:"1" }.
export async function createService(baseUrl, token, { name, status = '1' } = {}) {
  const data = await callJson(baseUrl, '/connectapi/v1/service', {
    method: 'POST',
    token,
    body: { name, status },
  });
  if (data.code !== '0') {
    throw new Error(
      `createService ${name} rejected: ${data.description || 'unknown'}`,
    );
  }
  return { serviceid: data.serviceid, servicekey: data.servicekey };
}

// POST /service/{sid}/flow is multipart (`flow` JSON + `file` binary).
// Target server unpacks the blob and validates custom-integration references
// by NAME — missing names return a 2xx with API-level code `2200` listing them.
//
// NOTE on `filename`: the capture HAR has `filename="100352.workflow"` — the
// SOURCE flow id, not the display name. Connect's unpacker appears to key off
// the filename id for internal routing; using the display name here yielded a
// 403 code 10001 "import flow internal API failed". Pass `sourceFlowId` and
// we'll build the filename as `${sourceFlowId}.workflow`.
export async function uploadFlow(
  baseUrl,
  token,
  serviceId,
  { flowName, flowType = 2, hexBlob, folderId = '0', sourceFlowId },
) {
  await pace();
  const bytes = hexToBytes(hexBlob);
  const url = new URL(`/connectapi/v1/service/${serviceId}/flow`, baseUrl);
  const fd = new FormData();
  fd.append(
    'flow',
    JSON.stringify({
      flowType,
      name: flowName,
      spid: '',
      status: 0,
      folderId,
      defLanguage: '',
      subLangs: '',
      copyFlowId: '',
      createFlowType: '2',
    }),
  );
  const fileBlob = new Blob([bytes], { type: 'application/octet-stream' });
  const filename = `${sourceFlowId || flowName}.workflow`;
  fd.append('file', fileBlob, filename);

  let res;
  let lastErr;
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 500));
    try {
      res = await fetch(url.toString(), {
        method: 'POST',
        headers: { accesstoken: token },
        body: fd,
      });
      break;
    } catch (e) {
      lastErr = e;
      if (!/Failed to fetch|NetworkError|network/.test(String(e.message))) throw e;
    }
  }
  if (!res) throw lastErr;

  const text = await res.text();
  if (!res.ok) {
    throw new Error(
      `uploadFlow ${serviceId}/${flowName} → ${res.status}: ${text.slice(0, 300)}`,
    );
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { raw: text };
  }
  if (Array.isArray(parsed) && parsed[0]?.code && parsed[0].code !== '0') {
    throw new Error(
      `uploadFlow ${flowName} rejected (${parsed[0].code}): ${parsed[0].description || ''}`,
    );
  }
  return parsed;
}

// Rename a custom integration. ENDPOINT NOT YET HAR-VERIFIED. The pattern
// `POST /customnode → PUT /customnode/{id}` is Connect's convention elsewhere,
// but the shape (full object vs delta) is unknown until an edit-save HAR is
// captured. The Transfer UI exposes this behind an opt-in switch, defaulting
// OFF, and surfaces failures without failing the whole transfer.
export async function renameCustomNode(baseUrl, token, nodeId, newName) {
  const data = await callJson(
    baseUrl,
    `/connectapi/v1/nodeMgmt/customnode/${nodeId}`,
    {
      method: 'PUT',
      token,
      body: { name: newName },
    },
  );
  if (data.code && data.code !== '0') {
    throw new Error(
      `renameCustomNode ${nodeId} rejected: ${data.description || 'unknown'}`,
    );
  }
  return data;
}

function hexToBytes(hex) {
  const clean = hex.trim();
  const len = clean.length;
  const out = new Uint8Array(len / 2);
  for (let i = 0; i < len; i += 2) {
    out[i / 2] = parseInt(clean.substr(i, 2), 16);
  }
  return out;
}
